/**
 * Beta v3 polish B (2026-05-27): coloured dot used on the Agreements
 * and Persons list pages for the PEP and US-Person (FATCA) columns.
 *
 * Color mapping (kept identical between the two columns so the user
 * scans one mental model):
 *   - grey   = unset / no parties / incomplete
 *   - green  = safe value ('Nao' for PEP; false for USP)
 *   - orange = anything-else value (PEP / Related for PEP; true for USP)
 *
 * Tooltip carries the human-readable value so a screen-reader user
 * (or anyone hovering) gets the same information as the colour does.
 */
import { Tooltip } from '@mantine/core';

import { pepLabel, usPersonLabel } from '../utils/pepLabels';

type DotColor = 'gray' | 'green' | 'orange';

const COLOR_TO_HEX: Record<DotColor, string> = {
  gray:   'var(--mantine-color-gray-5)',
  green:  'var(--mantine-color-green-6)',
  orange: 'var(--mantine-color-orange-6)',
};

function Dot({ color, label }: { color: DotColor; label: string }) {
  return (
    <Tooltip label={label} withArrow openDelay={150}>
      <span
        aria-label={label}
        style={{
          display: 'inline-block',
          width: 12,
          height: 12,
          borderRadius: '50%',
          background: COLOR_TO_HEX[color],
          verticalAlign: 'middle',
          cursor: 'help',
        }}
      />
    </Tooltip>
  );
}

/**
 * PEP dot. Accepts the AgreementOut aggregated value
 * ('PEP' | 'Related' | 'Nao' | null) or a raw PersonOut.pep_status string
 * (the stored Portuguese value). Anything non-empty that isn't 'Não' / 'Nao'
 * is treated as orange. Tooltips read in English (P4, `pepLabel`).
 */
export function PepDot({ value }: { value: string | null | undefined }) {
  if (value == null || value === '') {
    return <Dot color="gray"  label={`PEP: ${pepLabel(value)}`} />;
  }
  if (value === 'Não' || value === 'Nao') {
    return <Dot color="green" label={`PEP: ${pepLabel(value)}`} />;
  }
  // 'PEP' (aggregate or raw), 'Related' (aggregate), or the longer raw
  // values 'Membro de Família de PEP' / 'Pessoa Próxima de PEP'.
  return <Dot color="orange" label={`PEP: ${pepLabel(value)}`} />;
}

/** USP dot. true = orange "Yes"; false = green "No"; null = grey "Not answered". */
export function UspDot({ value }: { value: boolean | null | undefined }) {
  const color: DotColor = value === true ? 'orange' : value === false ? 'green' : 'gray';
  return <Dot color={color} label={`US person: ${usPersonLabel(value)}`} />;
}
